import { v4 as uuid } from 'uuid';

import { defaultAllowedFileTypes, defaultLargeFormatFileTypes } from './validation/default-file-whitelist';

export interface FileUploaderOptions {
    dropAreaText?: string;
    dropButtonLabel?: string;
    attachmentListHeading?: string;
    className?: string;
    maxFileByteSize?: number;
    maxCombinedUploadByteSize?: number;
    largeFormatFileMinByteSize?: number;
    allowedFileTypes?: string[];
    largeFormatFileTypes?: string[];
    fileErrorText?: string;
}

export interface RequiredFileUploaderOptions extends FileUploaderOptions {
    dropAreaText: string;
    dropButtonLabel: string;
    attachmentListHeading: string;
    maxFileByteSize: number;
    maxCombinedUploadByteSize: number;
    largeFormatFileMinByteSize: number;
    allowedFileTypes: string[];
    largeFormatFileTypes: string[];
}

export const ONE_MEGABYTE = 1024 * 1024;

export const defaultOptions: RequiredFileUploaderOptions = {
    dropAreaText: 'Dra filer hit, eller',
    dropButtonLabel: 'Velg fil',
    attachmentListHeading: 'Dokumenter',
    maxFileByteSize: 4 * ONE_MEGABYTE,
    maxCombinedUploadByteSize: 25 * ONE_MEGABYTE,
    largeFormatFileMinByteSize: 1024,
    allowedFileTypes: defaultAllowedFileTypes,
    largeFormatFileTypes: defaultLargeFormatFileTypes,
    fileErrorText: 'Prøv å laste opp vedlegget på nytt.',
};

export interface Attachment {
    id: string;
    status: AttachmentStatus;
    fileName: string;
    file: File;
    url: string;
    size: number;
}

export enum AttachmentStatus {
    Uploading,
    Ok,
    Error,
    ErrorTotalFileSize,
}

export const mapFilesToAttachment = (files: File[]): Attachment[] =>
    files.map((file: File) => ({
        id: uuid(),
        file,
        fileName: file.name,
        url: URL.createObjectURL(file),
        size: file.size,
        status: AttachmentStatus.Uploading,
    }));

export enum AttachmentActionType {
    ADD_ATTACHMENTS = 'ADD_ATTACHMENTS',
    REMOVE_ATTACHMENT = 'REMOVE_ATTACHMENT',
    UPDATE_ATTACHMENT_STATUS = 'UPDATE_ATTACHMENT_STATUS',
    REMOVE_FAILED_ATTACHMENTS = 'REMOVE_FAILED_ATTACHMENTS',
    UPDATE_ATTACMENTS_ON_FAILURE = 'UPDATE_ATTACMENTS_ON_FAILURE',
}

export interface AttachmentAction {
    type: AttachmentActionType;
    payload: Attachment[];
}

export const updateAttachments = (attachments: Attachment[], action: AttachmentAction): Attachment[] => {
    const { type, payload } = action;

    switch (type) {
        case AttachmentActionType.ADD_ATTACHMENTS:
            return attachments.concat(payload);
        case AttachmentActionType.REMOVE_ATTACHMENT:
            return attachments.filter(({ id }) => payload.find((attachment) => attachment.id !== id));
        case AttachmentActionType.UPDATE_ATTACHMENT_STATUS:
            return attachments.map((attachment) => {
                return {
                    ...attachment,
                    status: payload.find(({ id }) => id === attachment.id)?.status || attachment.status,
                };
            });
        case AttachmentActionType.REMOVE_FAILED_ATTACHMENTS:
            return attachments.filter(
                ({ status }) => status !== AttachmentStatus.Error && status !== AttachmentStatus.ErrorTotalFileSize,
            );
        case AttachmentActionType.UPDATE_ATTACMENTS_ON_FAILURE:
            return attachments.map((attachment) => {
                return {
                    ...attachment,
                    status: payload.find(({ id }) => id === attachment.id) ? AttachmentStatus.Error : attachment.status,
                };
            });
        default:
            return attachments;
    }
};
